import { useMutation, useQueryClient } from '@tanstack/react-query'
import { type ComponentProps, useId, useState } from 'react'
import { EMPTY_ADDRESS, formatAddress, US_STATES, validateAddress } from '../../account/address'
import { shippingAddressesKey } from '../../account/queries'
import { fieldErrors, messageForBillingError } from '../../api/errors'
import type { Household } from '../../api/households'
import {
  type AddressField,
  addressFieldFor,
  createShippingAddress,
  type NewShippingAddress,
  type ShippingAddress,
} from '../../api/shipping-addresses'
import type { Subscription } from '../../api/subscriptions'
import { firstName, lastName, possessive, type User } from '../../api/users'
import { track } from '../../runtime/analytics'
import { currentAuth, useSession } from '../../state/session'
import { Choice } from '../ui/Choice'
import { SelectField } from '../ui/SelectField'
import { TextField } from '../ui/TextField'
import {
  SettingEditButton,
  SettingForm,
  SettingRow,
  useSettingEditor,
  ValueSkeleton,
} from './Setting'
import { useUpdateSubscription } from './useUpdateSubscription'

interface ShippingAddressSettingProps {
  subscription: Subscription
  child: User
  household: Household
  /** Everyone in the household, to say whose kit ships where. */
  kids: User[]
  /** Every subscription in the household: the other kits are the ones that can move along. */
  subscriptions: Subscription[]
  /** The purchaser's address book; undefined while it loads. */
  addresses: ShippingAddress[] | undefined
}

/** The choice that reveals the form for an address the book does not have yet. */
const NEW_ADDRESS = 'new'

const listOf = new Intl.ListFormat('en-US', { type: 'conjunction' })

/**
 * Where a child's kit ships. The purchaser's addresses are shared, so the
 * editor is a choice among them, with a form for a new one — addresses are
 * added, never edited — and, when another kit could ship to the same door,
 * a checkbox to move it along. Moving this kit never moves a sibling's.
 */
export function ShippingAddressSetting({
  subscription,
  child,
  household,
  kids,
  subscriptions,
  addresses,
}: ShippingAddressSettingProps) {
  const parent = useSession((state) => state.user)
  const queryClient = useQueryClient()
  const update = useUpdateSubscription(household)
  const create = useMutation({
    mutationFn: (address: NewShippingAddress) =>
      createShippingAddress(subscription.id, address, { auth: currentAuth() }),
  })

  const current = addresses?.find((address) => address.subscriptionIds.includes(subscription.id))
  const [choice, setChoice] = useState(NEW_ADDRESS)
  const [draft, setDraft] = useState(EMPTY_ADDRESS)
  const [alsoMove, setAlsoMove] = useState<ReadonlySet<string>>(new Set())
  const [invalidField, setInvalidField] = useState<AddressField | null>(null)
  const groupName = useId()

  const kidOf = (candidate: Subscription) => kids.find((kid) => kid.id === candidate.userId)
  const liveKits = subscriptions.filter(
    (candidate) => candidate.tier === 'kit' && candidate.status !== 'cancelled',
  )
  const chosen = addresses?.find((address) => address.id === choice)
  // The other kits that do not already ship to the chosen door.
  const movable = liveKits.filter(
    (candidate) =>
      candidate.id !== subscription.id && !chosen?.subscriptionIds.includes(candidate.id),
  )

  const editor = useSettingEditor({
    validate: () => {
      if (choice === NEW_ADDRESS) {
        const problem = validateAddress(draft)
        setInvalidField(problem?.field ?? null)
        return problem?.message ?? null
      }
      setInvalidField(null)
      if (choice === current?.id && alsoMove.size === 0) {
        return 'Choose another address, or add a new one.'
      }
      return null
    },
    save: async () => {
      const moving = movable.filter((candidate) => alsoMove.has(candidate.id))
      let addressId = choice
      // Whether anything changed at Recharge, which is so as soon as one write lands.
      let landed = false
      try {
        if (choice === NEW_ADDRESS) {
          // Adding an address ships this subscription to it already.
          addressId = (await create.mutateAsync(draft)).id
          landed = true
        } else {
          moving.unshift(subscription)
        }
        // Every move is asked for, whatever becomes of the others: one kit
        // could not move while another's request was already on its way.
        const moves = await Promise.allSettled(
          moving.map((candidate) =>
            update.mutateAsync({
              subscription: candidate,
              change: { shippingAddressId: addressId },
            }),
          ),
        )
        landed ||= moves.some((move) => move.status === 'fulfilled')
        const failed = moves.find((move) => move.status === 'rejected')
        if (failed) throw failed.reason
        track({ kind: 'account_update', setting: 'shipping' })
      } finally {
        // The book is read again so every card agrees — after a refusal too,
        // since a kit that moved before another could not is at its new door
        // already — and not until then does the change count as saved.
        if (landed) {
          await queryClient.invalidateQueries({ queryKey: shippingAddressesKey(household) })
        }
      }
    },
    // Recharge refuses a part of an address by name; that lands beside its field.
    onError: (cause) => {
      const [attribute, message] = Object.entries(fieldErrors(cause))[0] ?? []
      const field = attribute ? addressFieldFor(attribute) : undefined
      if (!field || !message) return messageForBillingError(cause)
      setInvalidField(field)
      return message
    },
  })

  function start() {
    setChoice(current?.id ?? NEW_ADDRESS)
    setDraft({
      ...EMPTY_ADDRESS,
      firstName: parent ? firstName(parent) : '',
      lastName: parent ? lastName(parent) : '',
    })
    setAlsoMove(new Set())
    setInvalidField(null)
    editor.start()
  }

  function setField(field: AddressField, value: string) {
    setDraft((previous) => ({ ...previous, [field]: value }))
  }

  function toggleMove(id: string, checked: boolean) {
    setAlsoMove((previous) => {
      const next = new Set(previous)
      if (checked) next.add(id)
      else next.delete(id)
      return next
    })
  }

  /** Whose kits ship to an address, as the line beneath it. */
  function shipsHere(address: ShippingAddress): string | null {
    const names = liveKits
      .filter((candidate) => address.subscriptionIds.includes(candidate.id))
      .map(kidOf)
      .filter((kid): kid is User => kid !== undefined)
      .map((kid) => possessive(firstName(kid)))
    if (names.length === 0) return null
    return `${listOf.format(names)} ${names.length === 1 ? 'kit ships' : 'kits ship'} here`
  }

  const possessiveName = possessive(firstName(child))

  if (editor.open) {
    return (
      <SettingForm editor={editor} submitLabel="Save">
        <fieldset className="flex flex-col">
          <legend className="pb-1 text-sm text-ink-soft">Ship {possessiveName} kit to</legend>
          {addresses?.map((address) => (
            <Choice
              key={address.id}
              type="radio"
              name={groupName}
              value={address.id}
              checked={choice === address.id}
              onChange={() => setChoice(address.id)}
              description={shipsHere(address)}
              data-testid={`address-${address.id}`}
            >
              {formatAddress(address)}
            </Choice>
          ))}
          <Choice
            type="radio"
            name={groupName}
            value={NEW_ADDRESS}
            checked={choice === NEW_ADDRESS}
            onChange={() => setChoice(NEW_ADDRESS)}
            data-testid="address-new"
          >
            Add a new address
          </Choice>
        </fieldset>
        {choice === NEW_ADDRESS && (
          <AddressFields draft={draft} invalidField={invalidField} onChange={setField} />
        )}
        {movable.length > 0 && (
          <div className="flex flex-col">
            {movable.map((candidate) => {
              const kid = kidOf(candidate)
              return (
                kid && (
                  <Choice
                    key={candidate.id}
                    type="checkbox"
                    checked={alsoMove.has(candidate.id)}
                    onChange={(event) => toggleMove(candidate.id, event.target.checked)}
                    data-testid={`also-move-${candidate.id}`}
                  >
                    Also ship {possessive(firstName(kid))} kit here
                  </Choice>
                )
              )
            })}
          </div>
        )}
      </SettingForm>
    )
  }

  return (
    <SettingRow
      label="Ships to"
      value={
        addresses ? current ? formatAddress(current) : 'No address on file' : <ValueSkeleton />
      }
      action={
        addresses && (
          <SettingEditButton
            editor={editor}
            onClick={start}
            aria-label={`Change where ${possessiveName} kit ships`}
          >
            Change
          </SettingEditButton>
        )
      }
      testId="setting-address"
    />
  )
}

interface AddressFieldsProps {
  draft: NewShippingAddress
  invalidField: AddressField | null
  onChange(field: AddressField, value: string): void
}

/** The parts of a US address, with the autocomplete tokens that let a browser fill them. */
function AddressFields({ draft, invalidField, onChange }: AddressFieldsProps) {
  const field = (
    name: AddressField,
    label: string,
    extra: Partial<ComponentProps<typeof TextField>> = {},
  ) => (
    <TextField
      {...extra}
      label={label}
      labelStyle="floating"
      invalid={invalidField === name}
      value={draft[name]}
      onChange={(event) => onChange(name, event.target.value)}
      data-testid={`address-${name}`}
    />
  )

  return (
    <div className="grid gap-3 sm:grid-cols-2" data-testid="address-fields">
      {field('firstName', 'First name', { autoComplete: 'given-name' })}
      {field('lastName', 'Last name', { autoComplete: 'family-name' })}
      <div className="sm:col-span-2">
        {field('address1', 'Street address', { autoComplete: 'address-line1' })}
      </div>
      <div className="sm:col-span-2">
        {field('address2', 'Apt, suite, etc. (optional)', { autoComplete: 'address-line2' })}
      </div>
      {field('city', 'City', { autoComplete: 'address-level2' })}
      <SelectField
        label="State"
        autoComplete="address-level1"
        invalid={invalidField === 'province'}
        value={draft.province}
        onChange={(event) => onChange('province', event.target.value)}
        data-testid="address-province"
      >
        <option value="">Choose a state</option>
        {US_STATES.map((state) => (
          <option key={state} value={state}>
            {state}
          </option>
        ))}
      </SelectField>
      {field('zip', 'ZIP code', { autoComplete: 'postal-code', inputMode: 'numeric' })}
      {field('phone', 'Phone (optional)', { autoComplete: 'tel', type: 'tel' })}
    </div>
  )
}
